/* eslint-disable react/display-name */
/* eslint-disable react/jsx-one-expression-per-line */
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import { forwardRef, useContext } from 'react';
import { useDispatch } from 'react-redux';
import H6 from '@base/H6/H6';
import H4 from '@base/H4/H4';
import H7 from '@base/H7/H7';
import P2 from '@base/P2/P2';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import Paper from '@base/Paper/Paper';
import Grow from '@base/Grow/Grow';
import Item from '@models/Item';
import dateHelper from '@utils/dates';
import pushRoute from '@utils/pushRoute';
import imageHelper from '@utils/imageHelper';
import textHelper from '@utils/textHelper';
import PlayCircleBig from '@icon/PlayCircleBig';
import MoreVert from '@icon/MoreVert';
import SpecialClassEnrollments from '@icon/SpecialClassEnrollments';
import { Spacings, TextColors } from '../../../../styleConstants';
import { programmeTypes } from '../../../../constants';

const Wrapper = styled(Paper)`
  background-color: ${({ theme }) => theme.colors.white};
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  cursor: ${({ isUpcoming }) => (isUpcoming ? 'auto' : 'pointer')};
`;

const StySpecialClassEnrollments = styled(SpecialClassEnrollments)`
  margin-right: ${Spacings.SPACING_2B};
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  margin-right: ${Spacings.SPACING_4B};
`;

const MenuDiv = styled.div`
  cursor: pointer;
`;

const Thumbnail = styled.div`
  width: 120px;
  height: 120px;
  background: ${({ bgColor }) => bgColor};
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ClassDetailsBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const ClassInfo = styled.div`
  display: flex;
  align-items: center;
`;

const ClassDetails = styled.div`
  flex: 1;
`;

const ClassTitle = styled(H6)`
  margin: ${Spacings.SPACING_2B} 0;
  color: ${TextColors.BLACK};
`;

const SpecialClassCompletedFooter = styled.div`
  display: flex;
`;

const LiveAttendedImg = styled.img`
  margin-right: ${Spacings.SPACING_2B};
`;

const ScheduleClassCard = forwardRef(
  ({ classData, setTypeOfClass, setAnchorEl, setCurrentSession }, ref) => {
    const dispatch = useDispatch();
    const theme = useContext(ThemeContext);
    const data = classData;
    const courseData = classData.programmeNM;
    let {
      lessonDuration,
      rank,
      liveLearnersCount: attendedLiveCount
    } = classData;
    let {
      name: courseName,
      is_special: isSpecial,
      enrollment_count: enrollments,
      programme_type: progType,
      uid: courseUID
    } = courseData;
    const slugCourseName = textHelper.getStringWordsJoinedWith(courseName, '-');
    if (!rank) rank = 0;
    if (!enrollments) enrollments = 0;
    if (!lessonDuration) lessonDuration = 0;
    if (!attendedLiveCount) attendedLiveCount = 0;
    const isUpcoming = Item.isUpcoming(data);
    const isMissed = Item.classDidNotHappen(data, 3600 * 1000);

    const getIsLive = (dataObj) => {
      if (isMissed) return false;
      return dataObj.type === 'post'
        ? Item.isCurrentlyLive(dataObj)
        : dataObj.isLive;
    };

    const isLive = getIsLive(data);

    const isCompleted =
      data.type === 'post'
        ? Item.isRecorded(data)
        : !isMissed && Item.getTimeDifference(data) < 0;

    const renderCourseTitle = () => {
      if (isSpecial)
        return (
          <H7 lineClamp={2} color={TextColors.ORANGE}>
            FREE LIVE CLASS
          </H7>
        );
      return (
        <P2 lineClamp={1} color={TextColors.SECONDARY}>
          {courseName}
        </P2>
      );
    };

    const handleMenuClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const typeOfClass = {
        isUpcoming: false,
        isLive: false,
        isCompleted: false,
        isMissed: false
      };
      if (isUpcoming) typeOfClass.isUpcoming = true;
      if (isLive) typeOfClass.isLive = true;
      if (isCompleted) typeOfClass.isCompleted = true;
      if (isMissed) typeOfClass.isMissed = true;
      setAnchorEl(e.target);
      setCurrentSession(classData);
      setTypeOfClass(typeOfClass);
    };

    const renderClassInfo = () => {
      let classInfo = `Lesson ${rank}`;
      if (isLive)
        classInfo += ` Starts at ${dateHelper.formatTime(data.liveAt)}`;
      else if (isCompleted)
        classInfo += ` • ${dateHelper.formatTime(
          data.liveAt
        )} • ${dateHelper.getHoursMinutesValueFromSeconds(lessonDuration)}`;
      if (isSpecial) {
        classInfo = `${enrollments} enrollments`;
        if (isCompleted)
          classInfo = `${dateHelper.formatTime(
            data.liveAt
          )} • ${dateHelper.getHoursMinutesValueFromSeconds(lessonDuration)}`;
      }
      return classInfo;
    };

    const renderFooterText = () => {
      if (isMissed)
        return <P2 color={TextColors.RED}>You missed taking the class</P2>;
      if (isSpecial && isCompleted)
        return (
          <SpecialClassCompletedFooter>
            <LiveAttendedImg
              src={imageHelper.getImage('liveAttended.png')}
              height="20px"
              width="20px"
              alt="rescheduled"
            />
            <P1>{attendedLiveCount} attended live</P1>
          </SpecialClassCompletedFooter>
        );
      return null;
    };

    const renderLeftDiv = () => {
      let bgColor = null;
      if (isUpcoming) bgColor = '#F0F4F7';
      else if (isLive) bgColor = '#FDEFEF';
      else if (isCompleted) bgColor = '#EAF2FE';
      else if (isMissed) bgColor = '#FDEFEF';
      let content = null;
      if (isUpcoming)
        content = (
          <>
            <H4>{dateHelper.getTimeWithoutAmPm12HrFormat(data.liveAt)}</H4>
            <H5>{dateHelper.getAmPm(data.liveAt)}</H5>
          </>
        );
      else if (isMissed)
        content = (
          <>
            <H4 color={TextColors.RED}>
              {dateHelper.getTimeWithoutAmPm12HrFormat(data.liveAt)}
            </H4>
            <H5 color={TextColors.RED}>{dateHelper.getAmPm(data.liveAt)}</H5>
          </>
        );
      else if (isLive)
        content = (
          <>
            <PlayCircleBig
              size={Spacings.SPACING_10B}
              color={theme.colors.red}
            />
            <H5 color={theme.colors.red}>Go Live</H5>
          </>
        );
      else if (isCompleted)
        content = (
          <PlayCircleBig size={Spacings.SPACING_10B} color={TextColors.BLUE} />
        );
      return (
        <ThumbnailWrapper>
          <Thumbnail bgColor={bgColor}>{content}</Thumbnail>
        </ThumbnailWrapper>
      );
    };

    const onItemClick = () => {
      if (isLive) {
        dispatch({
          type: 'CLASS_MODAL_OPEN'
        });
        pushRoute(
          '/educator/live/[classUID]',
          `/educator/live/${classData.uid}`
        );
      } else {
        let progString = 'plus-courses';
        if (progType === programmeTypes.COURSE && isSpecial) {
          progString = 'class';
        } else if (progType === programmeTypes.TESTSERIES)
          progString = 'test-series';
        let href = '/educator/[courseType]/[courseSlug]/[courseUID]';
        const as = `/educator/${progString}/${slugCourseName}/${courseUID}`;
        if (progType === programmeTypes.COURSE && isSpecial) {
          href = '/educator/class/[slug]/[courseUID]';
        }

        pushRoute(href, as);
      }
    };

    return (
      <Wrapper
        gridcolumn="span 11"
        ref={ref}
        id={`class-date-${classData.date}`}
        padding={Spacings.SPACING_4B}
        onClick={onItemClick}
        isUpcoming={false}
      >
        {renderLeftDiv(classData)}
        <ClassDetailsBlock>
          <ClassDetails>
            {renderCourseTitle()}
            <ClassTitle lineClamp={2}>{data.name}</ClassTitle>
            <ClassInfo>
              {isSpecial && !isCompleted && (
                <StySpecialClassEnrollments color={TextColors.SECONDARY} />
              )}
              <P2 color={TextColors.SECONDARY}>{renderClassInfo()}</P2>
            </ClassInfo>
          </ClassDetails>
          <Grow />
          <div>{renderFooterText()}</div>
        </ClassDetailsBlock>
        <Grow />
        <MenuDiv>
          <MoreVert size="20px" onClick={handleMenuClick} />
        </MenuDiv>
      </Wrapper>
    );
  }
);

ScheduleClassCard.displayName = 'ScheduleClassCard';

export default ScheduleClassCard;

ScheduleClassCard.propTypes = {
  classData: PropTypes.objectOf(PropTypes.any),
  setTypeOfClass: PropTypes.func.isRequired,
  setAnchorEl: PropTypes.func.isRequired,
  setCurrentSession: PropTypes.func.isRequired
};

ScheduleClassCard.defaultProps = {
  classData: {}
};
